import {
	MIAW_CONFIG,
} from './config.js';

// Client-generated UUIDs for conversation + message ids (message ids let us
// match our own outgoing messages against the echoed SSE event and dedupe).
const genId = () => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

// Re-mint this far before the token's stated expiry to avoid using one that
// expires mid-request.
const TOKEN_SKEW_MS = 5 * 60 * 1000;
const DEFAULT_TTL_MS = 60 * 60 * 1000; // MIAW default is ~60 min.

// Thin REST client for the Salesforce MIAW (Messaging for In-App & Web) Enhanced
// Chat API against the SCRT2 base URL. Framework-agnostic (no React) so it's
// unit-testable by mocking fetch, and so a future bronco-proxy variant can swap
// only this transport layer. The streaming half lives in MiawEventStream.
//
// Payload shapes follow the MIAW v2 docs / SF's sample app; exact field shapes
// are validated end-to-end once the Custom Client deployment exists (DEV-11952).
export default class MiawClient {
	// options.tokenStore (optional): { load(): {token, expiresAt}|null, save(token, expiresAt) }.
	// Persisting the token across reloads keeps the same anonymous uid, so
	// conversations created earlier stay resumable (a fresh token can't touch them).
	// options.identityTokenProvider (optional): async () => <signed identity JWT>.
	// When set, ensureToken uses the AUTHENTICATED flow (stable uid → conversations
	// persist); otherwise the unauthenticated flow (anonymous, per-token uid).
	constructor(config = MIAW_CONFIG, options = {}) {
		this.config = config;
		this.tokenStore = options.tokenStore || null;
		this.identityTokenProvider = options.identityTokenProvider || null;
		this.token = null;
		this.tokenExpiresAt = 0;
	}

	get base() {
		return `${this.config.scrt2URL}/iamessage/api/v2`;
	}

	getToken() {
		return this.token;
	}

	// Mint (or reuse) an unauthenticated access token. No refresh grant exists —
	// re-mint before expiry (skew) and on a 401 (see request()).
	async ensureToken(force = false) {
		const now = Date.now();
		if (!force && this.token && now < this.tokenExpiresAt - TOKEN_SKEW_MS) {
			return this.token;
		}

		// Reuse a persisted token (e.g. across a page reload) if still valid — keeps
		// the same anonymous uid so earlier conversations remain resumable.
		if (!force && !this.token && this.tokenStore) {
			const stored = this.tokenStore.load();
			if (stored && stored.token && now < stored.expiresAt - TOKEN_SKEW_MS) {
				this.token = stored.token;
				this.tokenExpiresAt = stored.expiresAt;
				return this.token;
			}
		}

		let res;
		if (this.identityTokenProvider) {
			// Authenticated: exchange our backend-signed identity JWT for an access token.
			const customerIdentityToken = await this.identityTokenProvider();
			res = await fetch(`${this.base}/authorization/authenticated/access-token`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					orgId: this.config.orgId,
					esDeveloperName: this.config.esDeveloperName,
					capabilitiesVersion: this.config.capabilitiesVersion,
					platform: this.config.platform,
					authorizationType: 'JWT',
					customerIdentityToken,
				}),
			});
		} else {
			res = await fetch(`${this.base}/authorization/unauthenticated/access-token`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					orgId: this.config.orgId,
					esDeveloperName: this.config.esDeveloperName,
					capabilitiesVersion: this.config.capabilitiesVersion,
					platform: this.config.platform,
				}),
			});
		}

		if (!res.ok) {
			throw new Error(`MIAW token request failed: ${res.status}`);
		}

		const data = await res.json();
		this.token = data.accessToken;
		this.tokenExpiresAt = now + (data.expiresIn ? data.expiresIn * 1000 : DEFAULT_TTL_MS);
		if (this.tokenStore) {
			this.tokenStore.save(this.token, this.tokenExpiresAt);
		}
		return this.token;
	}

	authHeaders(includeContentType = false) {
		const headers = {
			'Authorization': `Bearer ${this.token}`,
		};
		if (includeContentType) {
			headers['Content-Type'] = 'application/json';
		}
		return headers;
	}

	// Authenticated request with one transparent re-mint + retry on 401 (expired
	// token). Returns parsed JSON, or {} for empty bodies.
	async request(path, options = {}, retried = false) {
		await this.ensureToken();

		const res = await fetch(`${this.base}${path}`, {
			...options,
			headers: {
				...this.authHeaders(!!options.body),
				...(options.headers || {}),
			},
			body: options.body ? JSON.stringify(options.body) : undefined,
		});

		if (res.status === 401 && !retried) {
			await this.ensureToken(true);
			return this.request(path, options, true);
		}

		if (!res.ok) {
			throw new Error(`MIAW request ${path} failed: ${res.status}`);
		}

		const text = await res.text();
		return text ? JSON.parse(text) : {};
	}

	// Create a conversation. routingAttributes carries identity + page context
	// (the hidden pre-chat equivalent) and is only settable at creation time.
	// Returns the client-generated conversationId.
	async createConversation(routingAttributes = {}) {
		const conversationId = genId();
		await this.request('/conversation', {
			method: 'POST',
			body: {
				conversationId,
				esDeveloperName: this.config.esDeveloperName,
				routingAttributes,
			},
		});
		return conversationId;
	}

	// Send a text message. The client-generated messageId is returned so the
	// caller can match it against the echoed SSE CONVERSATION_MESSAGE and dedupe.
	async sendMessage(conversationId, text, messageId = genId()) {
		await this.request(`/conversation/${conversationId}/message`, {
			method: 'POST',
			body: {
				esDeveloperName: this.config.esDeveloperName,
				message: {
					id: messageId,
					messageType: 'StaticContentMessage',
					staticContent: {
						formatType: 'Text',
						text,
					},
				},
			},
		});
		return messageId;
	}

	// History for rehydrating the panel on reload / after an SSE gap. Returns
	// { conversationEntries: [...] }.
	listEntries(conversationId) {
		return this.request(
			`/conversation/${conversationId}/entries?esDeveloperName=${encodeURIComponent(this.config.esDeveloperName)}`,
			{ method: 'GET' }
		);
	}

	// NOTE: /transcript returns a PDF download, NOT JSON message history — use
	// listEntries() for history. Not implemented: request() reads text()+JSON.parse,
	// which would throw on binary PDF data. A future "download transcript" action
	// needs a dedicated binary/blob fetch path, so this throws to prevent misuse.
	getTranscript() {
		throw new Error('getTranscript is not implemented — /transcript returns a PDF; use listEntries() for history');
	}

	// End the messaging session (explicit "end chat" / logout — NOT navigation).
	endSession(conversationId) {
		return this.request(`/conversation/${conversationId}/end-session`, {
			method: 'POST',
		});
	}
}

export { genId };
